import { Hub } from '@aws-amplify/core'
import {
  confirmResetPassword,
  confirmSignUp as amplifyConfirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn as amplifySignIn,
  signUp as amplifySignUp,
} from 'aws-amplify/auth'

import { rethrowLegacyAuthError } from './authError'
import { withCognitoClientMetadataOptions } from './cognitoClientMetadata'
import { onboardingSignOut } from './onboardingSignOut'

const authFlowType = () => process.env.REACT_APP_COGNITO_AUTH_FLOW || 'USER_SRP_AUTH'

const sessionWrapper = session => ({
  getIdToken: () => ({
    getJwtToken: () => session.tokens?.idToken?.toString() || '',
  }),
  getAccessToken: () => ({
    getJwtToken: () => session.tokens?.accessToken?.toString() || '',
  }),
})

const userWrapper = (user, attributes) => ({
  username: user.username,
  attributes,
  challengeName: undefined,
  challengeParam: undefined,
})

/** v6 rejects signIn when Cognito already has a user (v4 replaced the session inline). */
const clearExistingCognitoSession = async () => {
  try {
    await getCurrentUser()
    await onboardingSignOut()
  } catch {
    // not signed in
  }
}

/** v4-shaped Auth surface for onboarding call sites and civic-champs-shared. */
export const Auth = {
  signIn: async (username, password) => {
    try {
      await clearExistingCognitoSession()

      const { isSignedIn, nextStep } = await amplifySignIn({
        username,
        password,
        options: withCognitoClientMetadataOptions({ authFlowType: authFlowType() }),
      })

      if (!isSignedIn) {
        const step = nextStep.signInStep
        if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD') {
          return {
            username,
            attributes: {},
            challengeName: 'NEW_PASSWORD_REQUIRED',
            challengeParam: nextStep.additionalInfo,
          }
        }
        if (step === 'CONFIRM_SIGN_UP') {
          const err = new Error('User is not confirmed.')
          err.code = 'UserNotConfirmedException'
          throw err
        }
        if (step === 'RESET_PASSWORD') {
          const err = new Error('Password reset required for the user.')
          err.code = 'PasswordResetRequiredException'
          throw err
        }
        const err = new Error(`Unsupported sign-in step: ${step}`)
        err.code = 'UnsupportedSignInStepException'
        throw err
      }

      const user = await getCurrentUser()
      const attributes = await fetchUserAttributes()
      const wrappedUser = userWrapper(user, attributes)
      Hub.dispatch('auth', { event: 'signIn', data: wrappedUser })
      return wrappedUser
    } catch (error) {
      rethrowLegacyAuthError(error)
    }
  },

  signUp: async ({ username, password, attributes, clientMetadata }) => {
    try {
      await clearExistingCognitoSession()

      const userAttributes = {}
      if (attributes) {
        for (const [key, value] of Object.entries(attributes)) {
          if (value !== undefined && value !== null && value !== '') {
            userAttributes[key] = String(value)
          }
        }
      }

      const signUpOptions = { userAttributes }
      if (clientMetadata) {
        signUpOptions.clientMetadata = clientMetadata
      }

      const result = await amplifySignUp({
        username,
        password,
        options: withCognitoClientMetadataOptions(signUpOptions),
      })

      return {
        user: { username },
        userSub: result.userId,
      }
    } catch (error) {
      rethrowLegacyAuthError(error)
    }
  },

  confirmSignUp: async (username, code, options = {}) => {
    try {
      await amplifyConfirmSignUp({
        username,
        confirmationCode: code,
        options: withCognitoClientMetadataOptions(options),
      })
    } catch (error) {
      rethrowLegacyAuthError(error)
    }
  },

  resendSignUp: async username => {
    try {
      await resendSignUpCode({
        username,
        options: withCognitoClientMetadataOptions(),
      })
    } catch (error) {
      rethrowLegacyAuthError(error)
    }
  },

  signOut: async () => {
    await onboardingSignOut()
    Hub.dispatch('auth', { event: 'signOut' })
  },

  currentSession: async () => {
    try {
      const session = await fetchAuthSession()
      if (!session.tokens?.idToken) {
        const err = new Error('No current user')
        err.code = 'NotAuthorizedException'
        throw err
      }
      return sessionWrapper(session)
    } catch (error) {
      rethrowLegacyAuthError(error)
    }
  },

  currentAuthenticatedUser: async () => {
    try {
      const user = await getCurrentUser()
      const attributes = await fetchUserAttributes()
      return userWrapper(user, attributes)
    } catch (error) {
      rethrowLegacyAuthError(error)
    }
  },

  forgotPassword: username =>
    resetPassword({ username, options: withCognitoClientMetadataOptions() }).catch(rethrowLegacyAuthError),

  forgotPasswordSubmit: (username, code, password) =>
    confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword: password,
      options: withCognitoClientMetadataOptions(),
    }).catch(rethrowLegacyAuthError),
}
